import {
	eventLogDb,
	type StatsArticleRow,
	type StatsDayRow,
	type StatsHourFigures,
	type StatsIssueRow,
} from '@shared/events';

import { dayOf } from './projection';

/**
 * Reading the figures. Nothing here is stored: streaks and period totals
 * are computed from the daily rows each time, so a changed definition never
 * needs a migration. Every function returns zeros, not an error, on a
 * device with no usable database.
 */
export interface ReadingTotals {
	readMs: number;
	listenedMs: number;
	articlesCompleted: number;
	issuesCompleted: number;
	/** Days in the period with any reading or listening. */
	activeDays: number;
}

const EMPTY: ReadingTotals = {
	readMs: 0,
	listenedMs: 0,
	articlesCompleted: 0,
	issuesCompleted: 0,
	activeDays: 0,
};

/** Noon of the same calendar day: adding days from here is safe across clock changes. */
function noon(timestamp: number): Date {
	const date = new Date(timestamp);
	date.setHours(12, 0, 0, 0);
	return date;
}

function addDays(timestamp: number, days: number): number {
	const date = noon(timestamp);
	date.setDate(date.getDate() + days);
	return date.getTime();
}

/** Totals for the days from `from` to `to`, both included. */
export async function getReadingTotals(
	from: number,
	to: number,
): Promise<ReadingTotals> {
	try {
		const rows = await eventLogDb.statsDays
			.where('day')
			.between(dayOf(from), dayOf(to), true, true)
			.toArray();
		return rows.reduce(
			(totals, row) => ({
				readMs: totals.readMs + Math.round(row.readMs),
				listenedMs: totals.listenedMs + Math.round(row.listenedMs),
				articlesCompleted: totals.articlesCompleted + row.articlesCompleted,
				issuesCompleted: totals.issuesCompleted + row.issuesCompleted,
				activeDays:
					totals.activeDays + (row.readMs > 0 || row.listenedMs > 0 ? 1 : 0),
			}),
			EMPTY,
		);
	} catch {
		return EMPTY;
	}
}

export function getTodayStats(now: number = Date.now()): Promise<ReadingTotals> {
	return getReadingTotals(now, now);
}

/** The last seven days, today included. */
export function getWeekStats(now: number = Date.now()): Promise<ReadingTotals> {
	return getReadingTotals(addDays(now, -6), now);
}

/** The calendar year so far. */
export function getYearStats(now: number = Date.now()): Promise<ReadingTotals> {
	return getReadingTotals(new Date(new Date(now).getFullYear(), 0, 1).getTime(), now);
}

/** One column of a chart: the figures of one day, or of one hour of a day. */
export interface StatsBucket extends ReadingTotals {
	/** `YYYY-MM-DD` for a day, `YYYY-MM-DDTHH` for an hour; device time. */
	key: string;
	/** A moment inside it (noon of a day, the start of an hour), for formatting. */
	timestamp: number;
}

function bucket(
	key: string,
	timestamp: number,
	figures: Partial<StatsHourFigures> | undefined,
): StatsBucket {
	const readMs = Math.round(figures?.readMs ?? 0);
	const listenedMs = Math.round(figures?.listenedMs ?? 0);
	return {
		key,
		timestamp,
		readMs,
		listenedMs,
		articlesCompleted: figures?.articlesCompleted ?? 0,
		issuesCompleted: figures?.issuesCompleted ?? 0,
		activeDays: readMs > 0 || listenedMs > 0 ? 1 : 0,
	};
}

/**
 * The last `days` days one by one, oldest first and today last. A day with
 * no reading is there with zeros, so the result always has `days` entries.
 */
export async function getDailyStats(
	days = 7,
	now: number = Date.now(),
): Promise<Array<StatsBucket>> {
	const timestamps = Array.from({ length: days }, (_, index) =>
		addDays(now, index - (days - 1)),
	);
	let rows = new Map<string, StatsDayRow>();
	try {
		const stored = await eventLogDb.statsDays
			.where('day')
			.between(dayOf(timestamps[0]), dayOf(now), true, true)
			.toArray();
		rows = new Map(stored.map((row) => [row.day, row]));
	} catch {
		// No usable database: empty days.
	}
	return timestamps.map((timestamp) => {
		const day = dayOf(timestamp);
		return bucket(day, timestamp, rows.get(day));
	});
}

/**
 * Today hour by hour: 24 entries, midnight first. An hour holds what was
 * recorded in it, so a reading that ran across the hour is in the one it
 * ended in.
 */
export async function getHourlyStats(
	now: number = Date.now(),
): Promise<Array<StatsBucket>> {
	const day = dayOf(now);
	let hours: StatsDayRow['hours'];
	try {
		hours = (await eventLogDb.statsDays.get(day))?.hours;
	} catch {
		// No usable database: empty hours.
	}
	return Array.from({ length: 24 }, (_, hour) => {
		const start = new Date(now);
		start.setHours(hour, 0, 0, 0);
		return bucket(
			`${day}T${String(hour).padStart(2, '0')}`,
			start.getTime(),
			hours?.[hour],
		);
	});
}

/**
 * Consecutive days with reading or listening, ending today. A streak is
 * still alive if today has nothing yet but yesterday did: the reader has
 * until midnight to keep it.
 */
export async function getReadingStreak(now: number = Date.now()): Promise<number> {
	let active: Set<string>;
	try {
		const rows = await eventLogDb.statsDays.toArray();
		active = new Set(
			rows.filter((row) => row.readMs > 0 || row.listenedMs > 0).map((row) => row.day),
		);
	} catch {
		return 0;
	}
	let cursor = active.has(dayOf(now)) ? now : addDays(now, -1);
	let streak = 0;
	while (active.has(dayOf(cursor))) {
		streak += 1;
		cursor = addDays(cursor, -1);
	}
	return streak;
}

export interface IssueProgress extends StatsIssueRow {
	/**
	 * 0 to 1: pages or articles seen, of how many the issue has. `undefined`
	 * only when no event has stated the issue's size (events recorded before
	 * `units_total` existed).
	 */
	progress?: number;
}

export async function getIssueProgress(
	issueId: number,
): Promise<IssueProgress | undefined> {
	try {
		const row = await eventLogDb.statsIssues.get(issueId);
		if (!row) {
			return undefined;
		}
		return {
			...row,
			readMs: Math.round(row.readMs),
			progress: row.unitsTotal
				? Math.min(1, row.unitsSeen / row.unitsTotal)
				: undefined,
		};
	} catch {
		return undefined;
	}
}

/** The articles of an issue the reader has read any of, most recent first. */
export async function getIssueArticles(
	issueId: number,
): Promise<Array<StatsArticleRow>> {
	try {
		const rows = await eventLogDb.statsArticles
			.where('issueId')
			.equals(issueId)
			.toArray();
		return rows.sort((a, b) => b.lastReadAt - a.lastReadAt);
	} catch {
		return [];
	}
}

/** Issues the reader has opened, most recently read first. */
export async function getRecentIssues(limit = 10): Promise<Array<StatsIssueRow>> {
	try {
		return await eventLogDb.statsIssues
			.orderBy('lastReadAt')
			.reverse()
			.limit(limit)
			.toArray();
	} catch {
		return [];
	}
}
